import { useMemo, useCallback, useState } from 'react';
import Swal from 'sweetalert2';
import Api from '../../../../component/Api';

const ACTION_CONFIG = {
    approve: {
        endpoint: '/is/studentApprove',
        optimisticStatus: 'is_forwarded',
        confirmTitle: 'Approved Student(s)',
        confirmText: (count) => `Are you sure you want to approve ${count} student(s)?`,
        confirmButtonColor: '#581f37',
        confirmButtonText: 'Yes, Approve',
        successTitle: 'Approved!',
        successText: (count) => `${count} student(s) approved successfully.`,
        successIcon: 'success',
        failPrefix: 'Approvel failed',
    },
    reject: {
        endpoint: '/is/studentRevert',
        optimisticStatus: 'is_reverted',
        confirmTitle: 'Revert Student(s)',
        confirmText: (count) => `Are you sure you want to revert ${count} student(s)?`,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, Revert',
        successTitle: 'Reverted!',
        successText: (count) => `${count} student(s) reverted successfully.`,
        successIcon: 'info',
        failPrefix: 'Revert failed',
    },
};

export const ACTION_STATUSES = ['entered'];

export function useSubmitAll({ students, selectedStudents, setSelectedStudents, onActionComplete }) {
    const { http } = useMemo(() => Api(), []);
    const [processingAction, setProcessingAction] = useState(null);
    const [forwardedImcIds, setForwardedImcIds] = useState([]);
    const [optimisticStatuses, setOptimisticStatuses] = useState({});

    const selectableSelectedStudents = selectedStudents.filter(imcId => {
        const student = students.find(s => s.imc_id === imcId);
        if (!student) return false;
        return ACTION_STATUSES.includes(student.entry_status) && !forwardedImcIds.includes(imcId);
    });

    const selectableSelectedCount = selectableSelectedStudents.length;

    const handleSubmit = useCallback(async (imcIds, action) => {
        const cfg = ACTION_CONFIG[action];
        const ids = Array.isArray(imcIds) ? imcIds : [imcIds];
        const count = ids.length;

        const result = await Swal.fire({
            title: cfg.confirmTitle,
            text: cfg.confirmText(count),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: cfg.confirmButtonColor,
            cancelButtonColor: '#6c757d',
            confirmButtonText: cfg.confirmButtonText,
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            preConfirm: async () => {
                try {
                    const response = await http.post(cfg.endpoint, {
                        imc_ids: ids.map(id => parseInt(id, 10)),
                    });
                    return response;
                } catch (err) {
                    Swal.showValidationMessage(
                        `${cfg.failPrefix}: ${err?.response?.data?.message || 'Please try again.'}`
                    );
                }
            },
        });

        if (!result.isConfirmed) return;

        setProcessingAction(action);
        try {
            await Swal.fire({
                title: cfg.successTitle,
                text: cfg.successText(count),
                icon: cfg.successIcon,
                confirmButtonColor: cfg.confirmButtonColor,
                confirmButtonText: 'Done',
            });

            setOptimisticStatuses(prev => {
                const next = { ...prev };
                ids.forEach(id => { next[id] = cfg.optimisticStatus; });
                return next;
            });
            setForwardedImcIds(prev => [...new Set([...prev, ...ids])]);
            setSelectedStudents(prev => [...new Set([...prev, ...ids])]);

            onActionComplete?.();
        } finally {
            setProcessingAction(null);
        }
        //eslint-disable-next-line
    }, [http, setSelectedStudents, onActionComplete]);

    const handleApprove = useCallback(() => {
        return handleSubmit(selectableSelectedStudents, 'approve');
        //eslint-disable-next-line
    }, [handleSubmit, selectableSelectedStudents]);

    return {
        processingAction,
        isApproving: processingAction === 'approve',
        isRejecting: processingAction === 'reject',
        forwardedImcIds,
        optimisticStatuses,
        selectableSelectedCount,
        selectableSelectedStudents,
        handleSubmit,
        handleApprove,
    };
}